import { FiLock } from 'react-icons/fi';
import type { Message } from '@/lib/types';

function formatMessageTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isYesterday) {
    return (
      'Yesterday ' +
      date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    );
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

type MessageBubbleProps = {
  message: Message;
  currentUserId: number;
  onImagePress?: (imageUrl: string) => void;
};

export function MessageBubble({
  message,
  currentUserId,
  onImagePress,
}: MessageBubbleProps) {
  const isSent = message.sender_user_id === currentUserId;
  const attachments = message.attachments || [];
  const imageAttachments = attachments.filter((att) =>
    att.attachment_type.startsWith('image/')
  );

  return (
    <div className={`flex my-1 ${isSent ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl ${
          isSent
            ? 'bg-primary text-white rounded-br-sm'
            : 'bg-white border border-gray-200 rounded-bl-sm'
        }`}
      >
        {/* Sender name for received messages */}
        {!isSent && message.sender_first_name && (
          <p className='text-xs font-semibold text-primary mb-1'>
            {message.sender_first_name}
          </p>
        )}

        {/* Image attachments */}
        {imageAttachments.length > 0 && (
          <div className='space-y-2 mb-2'>
            {imageAttachments.map((attachment) => (
              <button
                key={attachment.id}
                onClick={() => onImagePress?.(attachment.download_url)}
                className='block focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-xl overflow-hidden'
              >
                <img
                  src={attachment.download_url}
                  alt={attachment.name}
                  className='w-[200px] h-[200px] object-cover rounded-xl'
                />
              </button>
            ))}
          </div>
        )}

        {/* Message text */}
        {message.content && (
          <p
            className={`text-[15px] leading-5 ${
              isSent ? 'text-white' : 'text-gray-900'
            }`}
          >
            {message.content}
          </p>
        )}

        {/* Timestamp and lock icon */}
        <div className='flex items-center gap-1 mt-1'>
          <span
            className={`text-[11px] ${
              isSent ? 'text-white/70' : 'text-gray-400'
            }`}
          >
            {formatMessageTime(message.timestamp)}
          </span>
          <FiLock
            className={`w-2.5 h-2.5 ${
              isSent ? 'text-white/70' : 'text-gray-400'
            }`}
          />
        </div>
      </div>
    </div>
  );
}
